import { useState, useCallback } from 'react';

export interface SearchOptions {
  /**
   * 搜索字段配置
   */
  fields: string[];
  /**
   * 搜索条件构造器
   * @param searchText 搜索文本
   * @param fields 搜索字段列表
   * @returns SQL查询条件字符串
   */
  buildCondition?: (searchText: string, fields: string[]) => string;
  /**
   * 默认的搜索条件构造器
   * 使用LIKE查询，支持多个字段的OR查询
   */
  defaultBuilder?: boolean;
}

export interface UseSearchReturn {
  /**
   * 搜索输入值
   */
  searchValue: string;
  /**
   * 设置搜索值
   */
  setSearchValue: (value: string) => void;
  /**
   * 是否正在搜索
   */
  isSearching: boolean;
  /**
   * 设置搜索状态
   */
  setIsSearching: (value: boolean) => void;
  /**
   * 执行搜索
   */
  handleSearch: () => void;
  /**
   * 重置搜索
   */
  handleResetSearch: () => void;
  /**
   * 处理回车搜索
   */
  handleSearchKeyPress: (e: React.KeyboardEvent) => void;
  /**
   * 构造搜索条件
   */
  buildSearchCondition: (searchText: string) => string;
  /**
   * 获取当前搜索条件
   */
  getCurrentSearchCondition: () => string;
}

/**
 * 默认的搜索条件构造器
 * 使用LIKE查询，支持多个字段的OR查询
 */
const defaultConditionBuilder = (
  searchText: string,
  fields: string[]
): string => {
  if (!searchText || !searchText.trim()) return '';

  const trimmedSearch = searchText.trim();
  const conditions = fields.map(field => `${field} LIKE '%${trimmedSearch}%'`);
  return `(${conditions.join(' OR ')})`;
};

/**
 * 搜索功能Hook
 * @param options 搜索配置选项
 * @returns 搜索相关的状态和方法
 */
export function useSearch(options: SearchOptions): UseSearchReturn {
  const { fields, buildCondition, defaultBuilder = true } = options;

  const [searchValue, setSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // 构造搜索条件的函数
  const buildSearchCondition = useCallback(
    (searchText: string): string => {
      if (buildCondition) {
        return buildCondition(searchText, fields);
      }

      if (defaultBuilder) {
        return defaultConditionBuilder(searchText, fields);
      }

      return searchText.trim();
    },
    [fields, buildCondition, defaultBuilder]
  );

  // 处理搜索
  const handleSearch = useCallback(() => {
    const searchCondition = buildSearchCondition(searchValue);
    setIsSearching(!!searchCondition);
  }, [searchValue, buildSearchCondition]);

  // 重置搜索
  const handleResetSearch = useCallback(() => {
    setSearchValue('');
    setIsSearching(false);
  }, []);

  // 处理搜索输入框回车事件
  const handleSearchKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  // 获取当前搜索条件
  const getCurrentSearchCondition = useCallback((): string => {
    return isSearching ? buildSearchCondition(searchValue) : '';
  }, [isSearching, searchValue, buildSearchCondition]);

  return {
    searchValue,
    setSearchValue,
    isSearching,
    setIsSearching,
    handleSearch,
    handleResetSearch,
    handleSearchKeyPress,
    buildSearchCondition,
    getCurrentSearchCondition,
  };
}
